import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { BiometricDeviceUpsertFormFields } from "@/components/biometricDevices/BiometricDeviceUpsertFormFields";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { createBiometricDevice } from "@/lib/admin/biometricDevicesStore";
import {
  biometricDeviceFormErrorMessage,
  biometricDeviceFormToPayload,
  emptyBiometricDeviceForm,
  type BiometricDeviceFormState,
} from "@/lib/admin/biometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { ArrowLeft, Fingerprint, Loader2, Save } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const AddBiometricDevice = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "biometric_device_can_create");
  const [form, setForm] = useState<BiometricDeviceFormState>(emptyBiometricDeviceForm);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const handleCancel = () => navigate("/biometric-devices");

  const save = async () => {
    if (isSaving || !canCreate) return;
    if (!form.model.trim()) {
      toast.error("Model is required.");
      return;
    }

    setIsSaving(true);
    try {
      const r = await createBiometricDevice(biometricDeviceFormToPayload(form));
      toast.success(r.message);
      navigate("/biometric-devices");
    } catch (e) {
      console.error("[AddBiometricDevice] Save failed", e);
      toast.error(biometricDeviceFormErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!canCreate) {
    return (
      <PermissionDenied
        title="Cannot add biometric devices"
        message="You don’t have permission to create biometric devices. Ask an administrator to grant biometric_device_can_create."
      />
    );
  }

  return (
    <Layout title="Add biometric device">
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              onClick={handleCancel}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground">Add biometric device</h1>
              <p className="text-sm text-muted-foreground mt-1">Register a device type for company hardware setup.</p>
            </div>
          </div>
          <Button variant="premium" className="gap-2 shrink-0" disabled={isSaving} onClick={() => void save()}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Fingerprint className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-foreground text-sm">Device catalog entry</h2>
              <p className="text-xs text-muted-foreground">Model, integration driver, and connection protocol.</p>
            </div>
          </div>
          <BiometricDeviceUpsertFormFields form={form} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
        </motion.div>
      </div>
    </Layout>
  );
};

export default AddBiometricDevice;
